import * as Sentry from "@sentry/nextjs";

type LogLevel = "error" | "warn" | "info" | "debug";

type LogContext = {
  userId?: string;
  component?: string;
  action?: string;
  additionalData?: Record<string, unknown>;
};

type BoundaryReportContext = {
  boundary: "root" | "dashboard" | "global";
  routeArea?: string;
  pathname?: string;
  digest?: string;
  component?: string;
};

class ErrorLogger {
  private isDevelopment = process.env.NODE_ENV === "development";

  log(level: LogLevel, message: string, context?: LogContext, error?: Error) {
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      level,
      message,
      context,
      ...(error && {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
      }),
    };

    // In development, log to console with formatting
    if (this.isDevelopment) {
      const prefix = `[${timestamp}] ${level.toUpperCase()}:`;

      switch (level) {
        case "error":
          console.error(prefix, message, logData);
          break;
        case "warn":
          console.warn(prefix, message, logData);
          break;
        case "info":
          console.info(prefix, message, logData);
          break;
        case "debug":
          console.debug(prefix, message, logData);
          break;
      }
    }

    // Send errors to Sentry for all environments (Sentry config handles dev/prod filtering)
    if (level === "error" && error) {
      Sentry.captureException(error, {
        extra: context,
        tags: {
          level: level,
        },
      });
    }
  }

  error(message: string, context?: LogContext, error?: Error) {
    this.log("error", message, context, error);
  }

  warn(message: string, context?: LogContext) {
    this.log("warn", message, context);
  }

  info(message: string, context?: LogContext) {
    this.log("info", message, context);
  }

  debug(message: string, context?: LogContext) {
    this.log("debug", message, context);
  }

  /**
   * Report structured boundary errors to Sentry with tagged context
   * Used by app/error.tsx, app/d/error.tsx, and app/global-error.tsx
   * Also attempts a fire-and-forget server report in production for observability.
   */
  reportBoundaryError(
    error: Error & { digest?: string },
    boundaryContext: BoundaryReportContext
  ) {
    const timestamp = new Date().toISOString();

    const tags: Record<string, string> = {
      boundary: boundaryContext.boundary,
      runtime: "client",
      ...(boundaryContext.routeArea && {
        route_area: boundaryContext.routeArea,
      }),
      ...(boundaryContext.digest && { has_digest: "true" }),
    };

    const extras: Record<string, unknown> = {
      timestamp,
      component: boundaryContext.component,
      ...(boundaryContext.pathname && { pathname: boundaryContext.pathname }),
      ...(boundaryContext.digest && { digest: boundaryContext.digest }),
      errorMessage: error.message,
    };

    // Log to console in development
    if (this.isDevelopment) {
      console.error(
        `[${timestamp}] BOUNDARY ERROR [${boundaryContext.boundary}]:`,
        {
          message: error.message,
          tags,
          extras,
        }
      );
    }

    // Send structured error to Sentry and keep the event id for the server-side
    // breadcrumb so operators can correlate Railway/Vercel logs back to Sentry.
    const sentryEventId = Sentry.captureException(error, {
      tags,
      extra: extras,
    });

    // Fire-and-forget: attempt server-side logging in production
    // This is non-blocking and swallows all errors to prevent crashing the boundary UI
    if (!this.isDevelopment) {
      this.reportBoundaryErrorToServer(error, boundaryContext, sentryEventId);
    }
  }

  /**
   * Internal method: attempt non-blocking server report of boundary errors.
   * Fails silently to prevent boundary UI crashes.
   */
  private reportBoundaryErrorToServer(
    error: Error & { digest?: string },
    boundaryContext: BoundaryReportContext,
    sentryEventId?: string
  ) {
    try {
      const payload = {
        boundary: boundaryContext.boundary,
        routeArea: boundaryContext.routeArea,
        pathname: boundaryContext.pathname,
        digest: boundaryContext.digest,
        component: boundaryContext.component,
        errorMessage: error.message,
        errorStack: error.stack,
        sentryEventId,
        timestamp: new Date().toISOString(),
      };

      // Use fetch with keepalive for fire-and-forget semantics
      // keepalive ensures request completes even if page unloads
      fetch("/api/client-error", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {
        // Silently swallow errors - this observability path must never crash the boundary
      });
    } catch {
      // Silently swallow - prevent any exception from breaking boundary UI
    }
  }
}

// Export singleton instance
export const logger = new ErrorLogger();
